import {
    Box,
    Button,
    Dialog, DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';

import React, {useEffect, useState} from "react";
import { IdentityProviderAccount } from "../domain/IdentityProviderAccount";
import Grid from "@mui/material/Grid2";
import FormField from "./FormField";
import {useTranslation} from "react-i18next";
import {useMsal} from "@azure/msal-react";
import useGASonDemand from "../hooks/useGASonDemand";
import {format} from "date-fns";
import {DATE_TIME_CONSTANT_FORMAT} from "../util/Constants";

interface IdentityProviderAccountDisplayProps {
    identityProviderAccount: IdentityProviderAccount | undefined;
    handleAccountModified : any;
}

export default function IdentityProviderAccountDisplay({identityProviderAccount, handleAccountModified} : IdentityProviderAccountDisplayProps) {
    const {t, i18n} = useTranslation();
    const  {instance} = useMsal();
    const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
    const {data, error, loading, fetchData} = useGASonDemand();
    const [allowDeleteButton, setAllowDeleteButton] = useState(false);
    const [enableAccountAdminButtons, setEnableAccountAdminButtons] = useState(true);
    const [enableDeleteDialogActionButtons, setEnableDeleteDialogActionButtons] = useState(true);

    const [enableForceSignOutDialogActionButtons, setEnableForceSignOutDialogActionButtons] = useState(false);
    const [showForceSignOutDialog, setShowForceSignOutDialog] = useState(false);

    useEffect(() => {
        // @ts-ignore
        const hasAccountAdminPermissions = instance?.getActiveAccount()?.idTokenClaims?.roles?.indexOf('Account.Admin') >= 0;
        setAllowDeleteButton(hasAccountAdminPermissions);
        setEnableAccountAdminButtons(identityProviderAccount !== null && hasAccountAdminPermissions);
    }, [instance, identityProviderAccount]);



    function deleteIdentityProviderAccount() {
        setEnableDeleteDialogActionButtons(true);
        setShowDeleteAccountDialog(true);
    }

    function forceSessionSignOut() {
        setEnableForceSignOutDialogActionButtons(true);
        setShowForceSignOutDialog(true);
    }

    function handleCancelDeleteAccountClicked() {
        setShowDeleteAccountDialog(false);
    }

    function handleCancelSignOutAccountClicked() {
        setShowForceSignOutDialog(false);
    }

    function handleDeleteAccountClicked() {
        console.log("Deleting account" , instance.getActiveAccount()?.idTokenClaims);

        setEnableDeleteDialogActionButtons(false);

        fetchData(`/account-details/${identityProviderAccount?.globalId}`, "DELETE")
            .then(value => {
            setShowDeleteAccountDialog(false);
            handleAccountModified();
        }).catch(reason => {console.log(reason)});
    }

    function handleSignOutClicked() {
        console.log("Invalidating sessions..");

        setEnableForceSignOutDialogActionButtons(false);

        fetchData(`/account-details/${identityProviderAccount?.globalId}/revokeSignInSessions`, "POST")
            .then(value => {
                setShowForceSignOutDialog(false);
                handleAccountModified();
        }).catch(reason => {console.log(reason)});
    }

    return (
        <>
        <Grid container spacing={3}>
            <Grid size={12}>
                <Box sx={{color:'primary.contrastText', bgcolor: 'primary.main', p: 1}}>
                    <Typography variant={"h6"}>{t('domain.common.identityProvider')}</Typography>
                </Box>
            </Grid>

            <Grid size={3}>
                <Stack spacing={2} sx={{pl: 2}}>
                    <FormField title={t('domain.common.displayName')} value={identityProviderAccount?.displayName} />
                    <FormField title={t('domain.common.region')} value={identityProviderAccount?.region} />
                </Stack>
            </Grid>
            <Grid size={3}>
                <Stack spacing={2} sx={{pl: 2}}>
                    <FormField title={t('domain.common.accountNumber')} value={identityProviderAccount?.localUserAccount} />
                    <FormField title={t('domain.common.affinity')} value={identityProviderAccount?.affinity} />
                </Stack>
            </Grid>
            <Grid size={3}>
                <Stack spacing={2}>
                    <FormField title={t('domain.common.globalAccountId')} value={identityProviderAccount?.globalId} />
                    <FormField title={t('domain.common.created')} value={identityProviderAccount ? format(identityProviderAccount.created, DATE_TIME_CONSTANT_FORMAT) : '-'} />
                </Stack>
            </Grid>
            <Grid size={3}>
                <Stack spacing={2}>
                    <FormField title={t('domain.common.signInSessionsValidFrom')} value={identityProviderAccount ? format(identityProviderAccount.signInSessionsValidFrom, DATE_TIME_CONSTANT_FORMAT) : '-'} />
                    {allowDeleteButton &&
                            <Stack spacing={2} sx={{p: 0}} direction={"row"}>
                                <Button
                                    variant='contained'
                                    onClick={forceSessionSignOut}
                                    disabled={!enableAccountAdminButtons}
                                    startIcon={<LogoutIcon/>}>{t('views.lookupCustomerAccountView.identityProviderAccountPanel.invalidateSignInButton')}...</Button>
                                <Button
                                    variant='contained'
                                    onClick={deleteIdentityProviderAccount}
                                    disabled={!enableAccountAdminButtons}
                                    startIcon={<DeleteIcon/> }>{t('common.delete')}...</Button>
                            </Stack>
                       }
                </Stack>
            </Grid>

        </Grid>

        <Dialog open={showDeleteAccountDialog}>
            <DialogTitle id="alert-dialog-title">
                {t('views.lookupCustomerAccountView.identityProviderAccountPanel.deleteAccountDialogTitle')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('views.lookupCustomerAccountView.identityProviderAccountPanel.deleteAccountDialogPrompt')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDeleteAccountClicked} disabled={!enableDeleteDialogActionButtons}>{t('common.cancel')}</Button>
                <Button onClick={handleDeleteAccountClicked} disabled={!enableDeleteDialogActionButtons} loading={loading}>{t('common.delete')}</Button>
            </DialogActions>
        </Dialog>


        <Dialog open={showForceSignOutDialog}>
            <DialogTitle id="forcesignout-dialog-title">
                {t('views.lookupCustomerAccountView.identityProviderAccountPanel.invalidateSignInDialogTitle')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText style={{ display: "inline-block", whiteSpace: "pre-line",  wordWrap: "break-word" }}>
                    {t('views.lookupCustomerAccountView.identityProviderAccountPanel.invalidateSignInDialogPrompt')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelSignOutAccountClicked} disabled={!enableForceSignOutDialogActionButtons}>{t('common.cancel')}</Button>
                <Button onClick={handleSignOutClicked} disabled={!enableForceSignOutDialogActionButtons} loading={loading}>{t('views.lookupCustomerAccountView.identityProviderAccountPanel.invalidateDialogActionButton')}</Button>
            </DialogActions>
        </Dialog>
        </>
    );

}